/**
 * State-specific land-records facts for the /public-records/$state pages.
 *
 * The generated narrative in `public-records-context.ts` is derived from our
 * coverage data, which means every state page reads the same way with the name
 * swapped. This file carries the part that is genuinely different per state:
 * which office actually holds recorded easements, the statute that makes a
 * recording effective, how copy fees are set, what is available online, and the
 * one thing that trips people up in that state.
 *
 * Rules for editing:
 *  - Every fact must be verifiable from a free official source, and the source
 *    URL lives on the entry.
 *  - Never quote dollar amounts. Fee schedules change; describe the structure
 *    and link to the authority that sets it.
 *  - If something cannot be verified for a state, leave the field out rather
 *    than filling space.
 */

export interface StateRecordsSource {
  /** Human-readable citation, e.g. "Tex. Prop. Code § 11.004". */
  citation: string;
  /** Official, free source for the citation. */
  url: string;
}

export interface StatePublicRecordsProfile {
  stateCode: string;
  /** The office that actually holds recorded land documents. */
  office: string;
  /** How the recording office is organized in this state. */
  officeExplainer: string;
  /** The statute that governs recording or indexing of land documents. */
  recordingStatute: StateRecordsSource;
  /** How copy and certification fees are set (structure only, no amounts). */
  feeAuthority: StateRecordsSource & { note: string };
  /** What is searchable online versus in person. */
  onlineAccess: string;
  /** The state-specific mistake that costs people the most time. */
  quirk: string;
  /** A concrete search tactic that works in this state. */
  searchTip: string;
}

export const STATE_PUBLIC_RECORDS_PROFILES: StatePublicRecordsProfile[] = [
  {
    stateCode: "AZ",
    office: "County Recorder",
    officeExplainer:
      "Arizona records land documents with the elected County Recorder in each of the fifteen counties. The Recorder handles the document index; the County Assessor maintains the parcel and mapping side, so an easement search and a parcel-boundary check are two different offices.",
    recordingStatute: {
      citation: "A.R.S. § 33-411 (recording gives constructive notice)",
      url: "https://www.azleg.gov/arsDetail/?title=33",
    },
    feeAuthority: {
      citation: "A.R.S. § 11-475 (recorder fees)",
      url: "https://www.azleg.gov/arsDetail/?title=11",
      note: "Arizona sets recording fees by statute as a single flat charge per document rather than per page, with separate charges for copies and for certification.",
    },
    onlineAccess:
      "The large Arizona recorders. Maricopa, Pima and Pinal among them, publish a free document index with viewable images going back decades. Rural counties often expose only a recent index and route image requests to the counter or to mail.",
    quirk:
      "Arizona easements are frequently created on the recorded subdivision plat rather than in a separate deed, so a name-based search of the grantor/grantee index can come back clean on a lot that is fully burdened. Always pull the plat as well as the deeds.",
    searchTip:
      "Search the grantor/grantee index under the developer or utility name, not just the current owner. Most Arizona utility easements were granted by the subdivider before any home existed.",
  },
  {
    stateCode: "CA",
    office: "County Recorder or Clerk-Recorder",
    officeExplainer:
      "California records with the County Recorder, which in many counties is combined with the County Clerk into a Clerk-Recorder office. Fifty-eight county offices operate independently; there is no statewide land-records index.",
    recordingStatute: {
      citation: "Cal. Gov. Code § 27201 (instruments the recorder must accept)",
      url: "https://leginfo.legislature.ca.gov/faces/codesTOCSelected.xhtml?tocCode=GOV",
    },
    feeAuthority: {
      citation: "Cal. Gov. Code § 27366 (fee set by the board of supervisors)",
      url: "https://leginfo.legislature.ca.gov/faces/codesTOCSelected.xhtml?tocCode=GOV",
      note: "Copy fees are set locally by each county's board of supervisors within statutory limits, so the amount differs county to county even though the authority is statewide.",
    },
    onlineAccess:
      "This is where California differs sharply from most states: state law restricts electronic distribution of official record images, so many county recorders publish a searchable index online but require a counter visit, mail order, or an authorized-subscriber account to obtain the document image itself.",
    quirk:
      "California recorders index by party name, not by parcel number. If you only have an APN, start at the Assessor to get the ownership history, then take those names to the Recorder's index.",
    searchTip:
      "Schedule B of a California preliminary title report cites recording dates and instrument numbers directly, ordering the documents by instrument number is far faster than reconstructing the chain by name.",
  },
  {
    stateCode: "CO",
    office: "County Clerk and Recorder",
    officeExplainer:
      "Colorado combines elections, motor vehicle and recording duties into one elected office, the County Clerk and Recorder. Recorded easements, plats and deeds all sit with that office in the county where the land lies.",
    recordingStatute: {
      citation: "C.R.S. § 38-35-109 (recording; notice to subsequent purchasers)",
      url: "https://leg.colorado.gov/colorado-revised-statutes",
    },
    feeAuthority: {
      citation: "C.R.S. § 30-1-103 (clerk and recorder fees)",
      url: "https://leg.colorado.gov/colorado-revised-statutes",
      note: "Fees are set by statute on a per-page basis for recording, with separate per-page copy and certification charges.",
    },
    onlineAccess:
      "Front Range counties run modern search portals with images available for immediate download. Mountain and eastern-plains counties commonly index online but deliver images by email request.",
    quirk:
      "Colorado abandoned book and page for modern filings in favor of a sequential reception number. A title commitment citing a reception number will not resolve in a book/page search field, and older documents cited by book and page will not resolve in the reception-number field.",
    searchTip:
      "Colorado water and ditch rights ride alongside easements far more than in most states, search the index for ditch company names as well as utility names.",
  },
  {
    stateCode: "DE",
    office: "Recorder of Deeds",
    officeExplainer:
      "Delaware has only three counties: New Castle, Kent and Sussex, so every recorded easement in the state sits in one of three Recorder of Deeds offices. That makes a statewide search unusually tractable compared with larger states.",
    recordingStatute: {
      citation: "25 Del. C. ch. 1 (recording of deeds and other instruments)",
      url: "https://delcode.delaware.gov/title25/index.html",
    },
    feeAuthority: {
      citation: "9 Del. C. ch. 96 (Recorder of Deeds; fees)",
      url: "https://delcode.delaware.gov/title9/index.html",
      note: "Recording and copy fees are fixed by statute per document, with an added charge for certified copies.",
    },
    onlineAccess:
      "All three counties publish an online index; image access and the depth of scanned records differ between them, and the oldest deed books are usually available only at the courthouse.",
    quirk:
      "Delaware still uses metes-and-bounds descriptions tied to old road and watercourse monuments in the two southern counties, so an easement description may reference features that no longer exist on the ground.",
    searchTip:
      "Sussex County's rapid subdivision growth means many easements were created by plat in the last thirty years, check the recorded plat before assuming a rural parcel is unburdened.",
  },
  {
    stateCode: "FL",
    office: "Clerk of the Circuit Court and Comptroller",
    officeExplainer:
      "Florida does not have a separate recorder. The elected Clerk of the Circuit Court and Comptroller in each of the 67 counties maintains the Official Records series, which is where deeds, easements and plats are recorded.",
    recordingStatute: {
      citation: "Fla. Stat. § 28.222 (clerk as recorder)",
      url: "https://www.flsenate.gov/Laws/Statutes/2023/Chapter28",
    },
    feeAuthority: {
      citation: "Fla. Stat. § 28.24 (service charges)",
      url: "https://www.flsenate.gov/Laws/Statutes/2023/28.24",
      note: "Copy and certification charges are set uniformly by statute across all counties, which makes Florida unusually predictable. The same document costs the same in Miami-Dade and in Liberty County.",
    },
    onlineAccess:
      "Florida is among the most open states for online land records: nearly every county clerk publishes a searchable Official Records index with document images, and most go back to the start of the county's imaging program.",
    quirk:
      "Florida easement burdens are frequently drainage related and created by plat dedications tied to a water management district. The plat dedication, not a standalone easement deed, is often the operative document.",
    searchTip:
      "Search the Official Records index by legal description keywords such as the subdivision name, then narrow by document type to 'easement'. Florida clerks index document type consistently.",
  },
  {
    stateCode: "IL",
    office: "County Recorder or County Clerk",
    officeExplainer:
      "Illinois is mid-consolidation. Counties historically had a separate elected Recorder, and many have now merged that function into the County Clerk. Cook County among them. The office name to look for therefore differs by county.",
    recordingStatute: {
      citation: "765 ILCS 5/28 (recording of instruments affecting land)",
      url: "https://www.ilga.gov/legislation/ilcs/ilcs.asp",
    },
    feeAuthority: {
      citation: "55 ILCS 5/3-5018 (recorder fees)",
      url: "https://www.ilga.gov/legislation/ilcs/ilcs.asp",
      note: "Illinois moved to predictable flat-rate recording fees per document type; copy charges remain per page and are set locally within statutory limits.",
    },
    onlineAccess:
      "Cook and the collar counties provide full online index and image access. Downstate counties vary widely, and several still direct copy requests to a mailed form.",
    quirk:
      "Because the recording function moved from Recorder to Clerk in a number of counties, older links and title commitments point to an office that no longer exists under that name. Start from the county's own website rather than a saved bookmark.",
    searchTip:
      "Illinois drainage districts hold widespread easement rights across farmland; check the district as well as the county index for rural parcels.",
  },
  {
    stateCode: "IN",
    office: "County Recorder",
    officeExplainer:
      "Indiana records with the elected County Recorder in each of the 92 counties. Parcel mapping sits separately with the County Assessor and the county GIS department.",
    recordingStatute: {
      citation: "Ind. Code § 32-21-4-1 (recording; notice)",
      url: "https://iga.in.gov/laws/2023/ic/titles/32",
    },
    feeAuthority: {
      citation: "Ind. Code § 36-2-7-10 (recorder fees)",
      url: "https://iga.in.gov/laws/2023/ic/titles/36",
      note: "Indiana uses a flat statutory fee per recorded document regardless of page count, with separate per-page copy and certification charges.",
    },
    onlineAccess:
      "Most Indiana counties contract with one of a handful of hosted search platforms, so the search screens look similar county to county even though each index is separate. Image availability generally starts in the 1980s or 1990s.",
    quirk:
      "Indiana requires a preparer statement and an affirmation that Social Security numbers have been redacted on recorded documents. Older records predate that rule, which is why some scanned images are partially blacked out on later re-recordings.",
    searchTip:
      "Indiana's rural drainage law gives county surveyors authority over regulated drains; a regulated drain easement may appear in the surveyor's records rather than the recorder's index.",
  },
  {
    stateCode: "MI",
    office: "Register of Deeds",
    officeExplainer:
      "Michigan records with the County Register of Deeds. In some counties the office is combined with the County Clerk, but the land-records series is the same either way.",
    recordingStatute: {
      citation: "MCL 565.201 (requirements for recording)",
      url: "https://www.legislature.mi.gov/Laws/MCL?objectName=mcl-565-201",
    },
    feeAuthority: {
      citation: "MCL 600.2567 (register of deeds fees)",
      url: "https://www.legislature.mi.gov/Laws/MCL?objectName=mcl-600-2567",
      note: "Michigan charges a flat statutory fee per recorded document, plus per-page copy fees and a separate certification fee.",
    },
    onlineAccess:
      "Michigan's larger registers publish searchable indexes with images; many mid-size counties provide the index free and charge for the image download.",
    quirk:
      "Michigan's recording-formatting statute is enforced strictly. Margins, type size and legibility can get a document rejected. That matters when reading old records too: a re-recorded easement with a later date is often the same grant fixed for formatting, not a new burden.",
    searchTip:
      "Lakefront and riparian access easements are a recurring Michigan issue; search the index for the plat's lake-access outlots as well as the lot itself.",
  },
  {
    stateCode: "MN",
    office: "County Recorder and Registrar of Titles",
    officeExplainer:
      "Minnesota runs two parallel land-title systems in every county. Abstract property is recorded with the County Recorder; Torrens (registered) property is filed with the Registrar of Titles, which is usually the same physical office under a second hat.",
    recordingStatute: {
      citation: "Minn. Stat. § 507.34 (recording; unrecorded conveyances void)",
      url: "https://www.revisor.mn.gov/statutes/cite/507.34",
    },
    feeAuthority: {
      citation: "Minn. Stat. § 357.18 (recorder fees)",
      url: "https://www.revisor.mn.gov/statutes/cite/357.18",
      note: "Recording and copy fees are set by statute statewide, with separate charges for certified copies and for Torrens filings.",
    },
    onlineAccess:
      "Hennepin, Ramsey and the surrounding metro counties offer strong online index and image access. Greater Minnesota counties frequently provide an index only, with images by request.",
    quirk:
      "This is the single most important Minnesota point: if the parcel is Torrens, easements appear as memorials listed on the Certificate of Title, and an easement not memorialized on that certificate generally does not burden the land. Searching only the abstract index on a Torrens parcel produces a false negative.",
    searchTip:
      "Ask the county which system the parcel is under before you search. The answer determines whether you read a grantor/grantee index or a certificate of title.",
  },
  {
    stateCode: "NV",
    office: "County Recorder",
    officeExplainer:
      "Nevada records with the County Recorder in each of the seventeen counties, including Carson City as an independent consolidated municipality.",
    recordingStatute: {
      citation: "NRS 247.120 (documents the recorder must record)",
      url: "https://www.leg.state.nv.us/NRS/NRS-247.html",
    },
    feeAuthority: {
      citation: "NRS 247.305 (recorder fees)",
      url: "https://www.leg.state.nv.us/NRS/NRS-247.html",
      note: "Nevada sets recorder fees by statute, generally per page for recording, with separate copy and certification charges.",
    },
    onlineAccess:
      "Clark and Washoe counties publish deep online indexes with images. The rural counties are small offices where a phone call is often faster than the web search.",
    quirk:
      "A very large share of Nevada is federally managed, and rights-of-way across federal land are not in the county index at all, they are BLM grants. If an access route leaves the parcel onto public land, the county recorder will show nothing.",
    searchTip:
      "Check the BLM land records system for right-of-way grants alongside the county index whenever the parcel adjoins federal land.",
  },
  {
    stateCode: "NY",
    office: "County Clerk, or the NYC City Register",
    officeExplainer:
      "Outside New York City, land records are filed with the County Clerk. Inside the city, the Department of Finance's Office of the City Register records for the Bronx, Brooklyn, Manhattan and Queens through ACRIS, while Staten Island's records stay with the Richmond County Clerk.",
    recordingStatute: {
      citation: "N.Y. Real Prop. Law § 291 (recording of conveyances)",
      url: "https://www.nysenate.gov/legislation/laws/RPP/291",
    },
    feeAuthority: {
      citation: "N.Y. C.P.L.R. § 8019 and county clerk fee schedules",
      url: "https://www.nysenate.gov/legislation/laws/CVP/8019",
      note: "County clerk fees are set by statute per document and per page; the New York City Register publishes its own schedule for the four boroughs it serves.",
    },
    onlineAccess:
      "ACRIS provides free public access to city records from the mid-1960s forward, with older records held on microfilm at the borough offices. Upstate county clerks run their own portals with widely varying image coverage.",
    quirk:
      "Manhattan, Brooklyn, Bronx and Queens searches go through ACRIS, but Staten Island does not, searching ACRIS for a Richmond County parcel returns nothing, which people routinely misread as 'no easements'.",
    searchTip:
      "New York easements affecting older city lots are often recorded as covenants and restrictions in liber and page form; search by block and lot rather than by name where the borough index supports it.",
  },
  {
    stateCode: "NC",
    office: "Register of Deeds",
    officeExplainer:
      "North Carolina records with the elected Register of Deeds in each of the 100 counties. Plats and condominium maps are recorded in a separate map series alongside the deed books.",
    recordingStatute: {
      citation: "N.C.G.S. § 47-20 (conveyances; registration; priority)",
      url: "https://www.ncleg.gov/EnactedLegislation/Statutes/HTML/ByChapter/Chapter_47.html",
    },
    feeAuthority: {
      citation: "N.C.G.S. § 161-10 (uniform fees of the register of deeds)",
      url: "https://www.ncleg.gov/EnactedLegislation/Statutes/HTML/ByChapter/Chapter_161.html",
      note: "North Carolina sets register of deeds fees uniformly by statute, with a flat charge covering a set number of pages and a per-page charge beyond it.",
    },
    onlineAccess:
      "North Carolina is well digitized. Most registers publish a free index with images, and several counties have scans reaching back into the nineteenth century.",
    quirk:
      "North Carolina is a strict race state for recording priority under § 47-20: an unrecorded easement generally loses to a later purchaser who records first. That makes the recorded index unusually decisive here. But it says nothing about prescriptive rights, which never appear in it.",
    searchTip:
      "Search the map series as well as the deed series. Utility and drainage easements on Piedmont subdivisions are usually shown on the recorded plat.",
  },
  {
    stateCode: "ND",
    office: "County Recorder",
    officeExplainer:
      "North Dakota records with the County Recorder. In several of the smallest counties the recorder's duties are combined with the auditor or treasurer, so one clerk may handle the entire land-records function.",
    recordingStatute: {
      citation: "N.D.C.C. ch. 47-19 (recording transfers)",
      url: "https://ndlegis.gov/cencode/t47c19.html",
    },
    feeAuthority: {
      citation: "N.D.C.C. § 11-18-05 (recorder fees)",
      url: "https://ndlegis.gov/cencode/t11c18.html",
      note: "Fees are set by statute per document with a page allowance, plus per-page copy and certification charges.",
    },
    onlineAccess:
      "Online coverage is thin outside the larger counties. Expect to phone or email the recorder's office for a search in much of the state, and expect a quick, helpful answer, these are small offices.",
    quirk:
      "In the Bakken counties, the index is dominated by oil and gas right-of-way, pipeline and saltwater-disposal easements, often granted decades ago by a prior mineral owner and still fully enforceable against the surface owner today.",
    searchTip:
      "North Dakota's abstract-of-title tradition is still strong, if a prior owner has an abstract, it may summarize the easement chain faster than any online search.",
  },
  {
    stateCode: "OH",
    office: "County Recorder",
    officeExplainer:
      "Ohio records with the elected County Recorder in each of the 88 counties. The County Auditor maintains parcel numbers and the transfer process, and the Engineer maintains road and survey records.",
    recordingStatute: {
      citation: "Ohio Rev. Code § 317.08 (records the recorder must keep)",
      url: "https://codes.ohio.gov/ohio-revised-code/section-317.08",
    },
    feeAuthority: {
      citation: "Ohio Rev. Code § 317.32 (recorder fees)",
      url: "https://codes.ohio.gov/ohio-revised-code/section-317.32",
      note: "Recording and copy fees are set by statute per page, with a separate certification charge.",
    },
    onlineAccess:
      "Most Ohio recorders offer a free online index with images. Several counties participate in shared regional search platforms, which lets you search neighboring counties from one screen.",
    quirk:
      "Ohio routes conveyances through the County Auditor for transfer before the Recorder will record them, and the County Engineer keeps road records and centerline surveys. A road or ditch easement may be documented in the Engineer's records rather than the Recorder's index.",
    searchTip:
      "For rural Ohio parcels, ask the County Engineer about township road right-of-way width before assuming the road easement matches what the deed says.",
  },
  {
    stateCode: "SC",
    office: "Register of Deeds, or the Clerk of Court",
    officeExplainer:
      "South Carolina splits this by county. Larger counties have a separate Register of Deeds (sometimes called the Register of Mesne Conveyances); in the remaining counties the Clerk of Court records land documents.",
    recordingStatute: {
      citation: "S.C. Code § 30-5-30 (recording of instruments)",
      url: "https://www.scstatehouse.gov/code/t30c005.php",
    },
    feeAuthority: {
      citation: "S.C. Code § 8-21-310 (fees of recording officers)",
      url: "https://www.scstatehouse.gov/code/t08c021.php",
      note: "South Carolina moved to flat statutory recording fees by document type, with separate per-page copy and certification charges.",
    },
    onlineAccess:
      "Coastal and upstate counties publish searchable indexes with images. Some smaller counties provide index-only access and route image requests to the counter.",
    quirk:
      "The 'Register of Mesne Conveyances' name confuses out-of-state searchers. It is the recording office, not a court function. Searching for a 'county recorder' in South Carolina will often turn up nothing.",
    searchTip:
      "Coastal South Carolina parcels frequently carry marsh, dock and beach-access easements tied to state permits; check the state's coastal management records in addition to the county index.",
  },
  {
    stateCode: "TX",
    office: "County Clerk",
    officeExplainer:
      "Texas records land documents with the elected County Clerk in each of the 254 counties, the largest number of independent recording offices in the country. There is no statewide index, so a search must be run county by county.",
    recordingStatute: {
      citation: "Tex. Prop. Code § 11.004 (recording and indexing duties)",
      url: "https://statutes.capitol.texas.gov/Docs/PR/htm/PR.11.htm",
    },
    feeAuthority: {
      citation: "Tex. Loc. Gov't Code § 118.011 (county clerk fees)",
      url: "https://statutes.capitol.texas.gov/Docs/LG/htm/LG.118.htm",
      note: "Fees are set by statute with a per-page structure for recording and separate charges for plain and certified copies.",
    },
    onlineAccess:
      "The metro counties publish deep, image-complete online indexes. Many rural counties provide only a recent index online and hold older volumes on the shelf in the clerk's office.",
    quirk:
      "Texas utility and pipeline easements are commonly written as blanket grants. 'over and across' the tract with no fixed location or width. The corridor's location was then set by where the line was actually built, which is why the deed alone will not tell you where it runs.",
    searchTip:
      "For pipeline corridors, cross-check the Railroad Commission's public mapping viewer against the county index; the recorded easement often predates any usable survey description.",
  },
  {
    stateCode: "UT",
    office: "County Recorder",
    officeExplainer:
      "Utah records with the County Recorder, an office that also maintains the tract index and the county's ownership plats. An unusually integrated setup compared with states that split index and mapping between two offices.",
    recordingStatute: {
      citation: "Utah Code § 57-3-102 (recording; constructive notice)",
      url: "https://le.utah.gov/xcode/Title57/Chapter3/57-3-S102.html",
    },
    feeAuthority: {
      citation: "Utah Code § 17-21-18.5 (recorder fees)",
      url: "https://le.utah.gov/xcode/Title17/Chapter21/17-21-S18.5.html",
      note: "Utah sets recorder fees by statute per document, with separate per-page copy and certification charges.",
    },
    onlineAccess:
      "Wasatch Front counties provide searchable indexes with image access, often tied to the parcel serial number. Rural counties frequently require a call or an in-person visit for images.",
    quirk:
      "Utah recorders index by tax serial number as well as by name, which makes a parcel-based search genuinely reliable here, unlike states where the parcel number never touches the recorded index.",
    searchTip:
      "Irrigation and canal company easements are widespread across Utah's valleys; search the index for the canal company name as well as the utility.",
  },
  {
    stateCode: "VA",
    office: "Circuit Court Clerk",
    officeExplainer:
      "Virginia has no county recorder. Land records are recorded with the Clerk of the Circuit Court in the county or independent city where the land sits, and the deed books physically live at the courthouse.",
    recordingStatute: {
      citation: "Va. Code § 17.1-227 (writings to be recorded by the clerk)",
      url: "https://law.lis.virginia.gov/vacode/title17.1/chapter2/section17.1-227/",
    },
    feeAuthority: {
      citation: "Va. Code § 17.1-275 (fees collected by circuit court clerks)",
      url: "https://law.lis.virginia.gov/vacode/title17.1/chapter2/section17.1-275/",
      note: "Clerk fees are fixed statewide by statute, including per-page copy charges and a separate certification fee.",
    },
    onlineAccess:
      "Virginia's remote land-records access generally runs through a secure subscriber system rather than an open public portal, so free image access usually means a public terminal at the clerk's office. Some independent cities publish more openly than the counties around them.",
    quirk:
      "Virginia's independent cities are not inside any county. A parcel in an independent city is recorded at that city's circuit court, not at the surrounding county's, searching the county returns nothing.",
    searchTip:
      "Ask the clerk's office directly for the deed book and page from the title commitment; Virginia clerks routinely pull and mail copies for the statutory fee.",
  },
  {
    stateCode: "WA",
    office: "County Auditor",
    officeExplainer:
      "Washington records with the County Auditor's recording division. Not a recorder or register of deeds. In a few charter counties the function sits in a records or elections department under a different name.",
    recordingStatute: {
      citation: "RCW 65.04.030 (auditor to record instruments)",
      url: "https://app.leg.wa.gov/RCW/default.aspx?cite=65.04.030",
    },
    feeAuthority: {
      citation: "RCW 36.18.010 (county auditor's fees)",
      url: "https://app.leg.wa.gov/RCW/default.aspx?cite=36.18.010",
      note: "Recording fees are set by statute as a first-page charge plus per-page charges, with separate copy and certification fees.",
    },
    onlineAccess:
      "Puget Sound counties provide full online index and image access. Eastern Washington counties vary, and several deliver images only by request.",
    quirk:
      "Washington requires a standardized first page or cover sheet on recorded documents under RCW 65.04.045. That cover sheet is what the index reads from, so an easement can be indexed under a shortened or abbreviated legal description that does not match the body of the document.",
    searchTip:
      "Read the document body, not the cover sheet, for the actual easement description, the two disagree more often in Washington than people expect.",
  },
];

const BY_CODE = new Map(STATE_PUBLIC_RECORDS_PROFILES.map((p) => [p.stateCode, p]));

export function stateRecordsProfile(code: string): StatePublicRecordsProfile | undefined {
  return BY_CODE.get(code);
}
